import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { toast } from 'components/Toast';
import styles from "./contractor.module.scss";
import { cn } from "utils";
import Input from "components/Input";
import Select from "components/Select";
import Radio from "components/Radio";
import Button from "components/Button";
import AddressAutocomplete from "components/AddressAutocomplete";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ContractorInputs, contractorSchema } from "./const";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createContractor,
    updateContractor,
    getContractor,
    getContractorBankAccounts,
    createContractorBankAccount,
    updateContractorBankAccount,
    deleteContractorBankAccount,
    getContractorNetPaySplits,
    createContractorNetPaySplit,
    inviteContractor,
} from "services/contractors";
import { getWorkplaces } from "services/workplaces";
import { getDepartments } from "services/departments";
import axiosInstance from "../../axios/axiosInstance";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";

const emptyBankAccount = () => ({ accountType: "checking", accountNumber: "", routingNumber: "" });
const maskAccount = (num) => (num ? "****" + String(num).slice(-4) : "****");

const PAYMENT_METHODS = [
    { label: "Check", value: "Check" },
    { label: "Direct Deposit", value: "DirectDeposit" },
];

const CONTRACTOR_TYPES = [
    { label: "Individual", value: "Individual" },
    { label: "Business", value: "Business" },
];

const PAY_TYPES = [
    { label: "Hourly", value: "Hourly" },
    { label: "Flat Fee", value: "FlatFee" },
    { label: "Monthly", value: "Monthly" },
    { label: "Weekly", value: "Weekly" },
];

const SERVICE_TYPES = [
    { label: "Technology / IT", value: "Technology / IT" },
    { label: "Marketing / Design", value: "Marketing / Design" },
    { label: "Consulting", value: "Consulting" },
    { label: "Finance / Accounting", value: "Finance / Accounting" },
    { label: "Legal", value: "Legal" },
    { label: "Construction / Trades", value: "Construction / Trades" },
    { label: "Healthcare", value: "Healthcare" },
    { label: "Writing / Content", value: "Writing / Content" },
    { label: "Other", value: "Other" },
];

const RELATIONSHIP_TYPES = [
    { label: "Spouse", value: "Spouse" },
    { label: "Parent", value: "Parent" },
    { label: "Child", value: "Child" },
    { label: "Sibling", value: "Sibling" },
    { label: "Friend", value: "Friend" },
    { label: "Other", value: "Other" },
];

const US_STATES = [
    { label: "Alabama", value: "AL" }, { label: "Alaska", value: "AK" },
    { label: "Arizona", value: "AZ" }, { label: "Arkansas", value: "AR" },
    { label: "California", value: "CA" }, { label: "Colorado", value: "CO" },
    { label: "Connecticut", value: "CT" }, { label: "Delaware", value: "DE" },
    { label: "Florida", value: "FL" }, { label: "Georgia", value: "GA" },
    { label: "Hawaii", value: "HI" }, { label: "Idaho", value: "ID" },
    { label: "Illinois", value: "IL" }, { label: "Indiana", value: "IN" },
    { label: "Iowa", value: "IA" }, { label: "Kansas", value: "KS" },
    { label: "Kentucky", value: "KY" }, { label: "Louisiana", value: "LA" },
    { label: "Maine", value: "ME" }, { label: "Maryland", value: "MD" },
    { label: "Massachusetts", value: "MA" }, { label: "Michigan", value: "MI" },
    { label: "Minnesota", value: "MN" }, { label: "Mississippi", value: "MS" },
    { label: "Missouri", value: "MO" }, { label: "Montana", value: "MT" },
    { label: "Nebraska", value: "NE" }, { label: "Nevada", value: "NV" },
    { label: "New Hampshire", value: "NH" }, { label: "New Jersey", value: "NJ" },
    { label: "New Mexico", value: "NM" }, { label: "New York", value: "NY" },
    { label: "North Carolina", value: "NC" }, { label: "North Dakota", value: "ND" },
    { label: "Ohio", value: "OH" }, { label: "Oklahoma", value: "OK" },
    { label: "Oregon", value: "OR" }, { label: "Pennsylvania", value: "PA" },
    { label: "Rhode Island", value: "RI" }, { label: "South Carolina", value: "SC" },
    { label: "South Dakota", value: "SD" }, { label: "Tennessee", value: "TN" },
    { label: "Texas", value: "TX" }, { label: "Utah", value: "UT" },
    { label: "Vermont", value: "VT" }, { label: "Virginia", value: "VA" },
    { label: "Washington", value: "WA" }, { label: "West Virginia", value: "WV" },
    { label: "Wisconsin", value: "WI" }, { label: "Wyoming", value: "WY" },
];

const toDateInput = (val) => { if (!val) return ''; return val.substring(0, 10); };

const AddEditContractor = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const contractorId = searchParams.get('id');
    const isEditMode = !!contractorId;
    // Data pre-filled from the Pending Onboardings page
    const prefillData = location.state?.contractorData ?? null;
    const fromOnboarding = location.state?.fromOnboarding === true;

    const { data: workplacesRaw = [] } = useQuery({ queryKey: ['workplaces'], queryFn: getWorkplaces });
    const { data: departmentsRaw = [] } = useQuery({ queryKey: ['departments'], queryFn: getDepartments });
    const workplaceOptions = workplacesRaw.map(w => ({ value: w.id, label: w.name }));
    const departmentOptions = departmentsRaw.map(d => ({ value: d.id, label: d.name }));

    const companyId = localStorage.getItem("companyId");
    const { data: locationsRaw } = useQuery({
        queryKey: ["company-locations", companyId],
        queryFn: () => axiosInstance.get(`/companies/${companyId}/locations`).then(r => r.data),
        enabled: !!companyId,
        retry: false,
    });
    const locationOptions = [
        { label: "— No specific location —", value: "" },
        ...(Array.isArray(locationsRaw) ? locationsRaw : []).map(l => ({ label: l.name, value: l.id })),
    ];

    const { data: contractorData } = useQuery({
        queryKey: ['contractor', contractorId],
        queryFn: () => getContractor(contractorId),
        enabled: isEditMode,
    });

    const [bankAccounts, setBankAccounts] = useState([emptyBankAccount()]);
    const [addModeSplits, setAddModeSplits] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [sendInvite, setSendInvite] = useState(true);
    const [inviteResult, setInviteResult] = useState(null);
    const [splits, setSplits] = useState([]);
    const [splitMessage, setSplitMessage] = useState(null);
    const [editingBankAccountId, setEditingBankAccountId] = useState(null);
    const [editBankAccountData, setEditBankAccountData] = useState({});
    const [confirmRemoveId, setConfirmRemoveId] = useState(null);
    const [showSwitchToCheckConfirm, setShowSwitchToCheckConfirm] = useState(false);
    const [showAddBankForm, setShowAddBankForm] = useState(false);
    const [newBankAccount, setNewBankAccount] = useState(emptyBankAccount());
    const highlightSetup = searchParams.get('highlight') === 'setup';
    const wageRef = useRef(null);

    const { data: bankAccountsRaw, isLoading: loadingBankAccounts, refetch: refetchBankAccounts } = useQuery({
        queryKey: ['contractor-bank-accounts', contractorId],
        queryFn: () => getContractorBankAccounts(contractorId),
        enabled: isEditMode,
        retry: false,
    });
    const existingBankAccounts = Array.isArray(bankAccountsRaw) ? bankAccountsRaw : (bankAccountsRaw?.data ?? bankAccountsRaw?.items ?? []);

    const { data: splitsRaw, refetch: refetchSplits } = useQuery({
        queryKey: ['contractor-net-pay-splits', contractorId],
        queryFn: () => getContractorNetPaySplits(contractorId),
        enabled: isEditMode,
        retry: false,
        refetchOnMount: "always",
    });
    const existingSplits = (() => {
        const raw = Array.isArray(splitsRaw) ? splitsRaw : (splitsRaw?.data ?? splitsRaw?.items ?? []);
        return Array.isArray(raw) ? raw : (raw && typeof raw === "object" ? Object.values(raw) : []);
    })();
    const splitsInitKey = useMemo(
        () =>
            isEditMode && contractorId
                ? `${contractorId}|${existingBankAccounts.length}|${(existingSplits || [])
                      .map((s) => `${s.bankAccountId || ""}-${s.percent ?? ""}-${s.amount ?? ""}`)
                      .join(",")}`
                : "",
        [isEditMode, contractorId, existingBankAccounts.length, existingSplits]
    );
    const lastSplitsInitKey = useRef("");

    useEffect(() => {
        if (!isEditMode || !existingBankAccounts?.length || !splitsInitKey) return;
        if (splitsInitKey === lastSplitsInitKey.current) return;
        lastSplitsInitKey.current = splitsInitKey;
        if (existingSplits?.length > 0) {
            setSplits(
                existingSplits.map((s) => ({
                    bankAccountId: s.bankAccountId || "",
                    splitType: s.percent > 0 ? "percentage" : s.amount > 0 ? "flat" : "remaining_balance",
                    value: s.percent > 0 ? s.percent : s.amount > 0 ? s.amount : null,
                }))
            );
        } else if (existingBankAccounts.length === 1) {
            setSplits([{ bankAccountId: existingBankAccounts[0].id, splitType: "remaining_balance", value: null }]);
        } else {
            const pct = Math.floor(100 / existingBankAccounts.length);
            setSplits(
                existingBankAccounts.map((b, i) => ({
                    bankAccountId: b.id,
                    splitType: i < existingBankAccounts.length - 1 ? "percentage" : "remaining_balance",
                    value: i < existingBankAccounts.length - 1 ? pct : null,
                }))
            );
        }
    }, [isEditMode, splitsInitKey, existingBankAccounts, existingSplits]);

    useEffect(() => {
        if (!isEditMode) lastSplitsInitKey.current = "";
    }, [isEditMode]);

    useEffect(() => {
        if (isEditMode && contractorId) {
            lastSplitsInitKey.current = "";
            queryClient.invalidateQueries({ queryKey: ["contractor-net-pay-splits", contractorId] });
        }
    }, [isEditMode, contractorId, queryClient]);

    const { mutate: mutateCreate, isPending: isCreating, isError: isCreateError, error: createError } = useMutation({
        mutationFn: createContractor,
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['contractors'] }); navigate('/contractor'); },
    });
    const { mutate: mutateUpdate, isPending: isUpdating, isError: isUpdateError, error: updateError } = useMutation({
        mutationFn: updateContractor,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contractors'] });
            const isPendingOnboarding = fromOnboarding ||
                contractorData?.onboardingStatus === "pending_review" ||
                contractorData?.onboardStatus === "pending_review" ||
                prefillData?.onboardingStatus === "pending_review" ||
                prefillData?.onboardStatus === "pending_review";
            navigate(isPendingOnboarding ? '/onboarding' : '/contractor');
        },
    });

    const { register, handleSubmit, reset, control, watch, trigger, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(contractorSchema),
        mode: 'onChange',
    });
    const paymentMethod = watch(ContractorInputs.PAYMENT_METHOD) || "";
    const watchedType = watch(ContractorInputs.CONTRACTOR_TYPE) || "";
    const isBusinessType = watchedType.toLowerCase() === 'business';
    const isIndividualType = !watchedType || watchedType.toLowerCase() === 'individual';

    // Auto-scroll to Wage section and validate all fields when arriving from "Complete Setup" link
    useEffect(() => {
        if (highlightSetup && contractorData && wageRef.current) {
            setTimeout(() => {
                trigger(); // show errors on all empty required fields
                wageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 400);
        }
    }, [highlightSetup, contractorData, trigger]);

    useEffect(() => {
        // Use API result when available; fall back to navigation state data from Pending Onboardings
        const raw = contractorData ?? prefillData;
        if (!raw) return;
        const c = (raw.error || !raw.id) && prefillData ? prefillData : raw;
        if (!c) return;
        reset({
            [ContractorInputs.FIRSTNAME]: c.firstName || '',
            [ContractorInputs.MIDDLENAME]: c.middleName || '',
            [ContractorInputs.LASTNAME]: c.lastName || '',
            [ContractorInputs.CONTACT_EMAIL]: c.email || '',
            [ContractorInputs.CONTRACT_START_DATE]: toDateInput(c.startDate),
            [ContractorInputs.TERMINATION_DATE]: toDateInput(c.terminationDate),
            [ContractorInputs.PAYMENT_METHOD]: c.defaultPaymentMethod || '',
            [ContractorInputs.CONTRACTOR_TYPE]: c.type || '',
            [ContractorInputs.DEPARTMENT_ID]: c.departmentId || '',
            [ContractorInputs.PRIMARY_WORKPLACE_ID]: c.primaryWorkplaceId || '',
            [ContractorInputs.LOCATION_ID]: c.locationId || '',
            [ContractorInputs.IS_WORK_FROM_HOME]: c.workFromHome || 'no',
            [ContractorInputs.SSN]: c.ssn || '',
            [ContractorInputs.EIN]: c.ein || '',
            [ContractorInputs.DATE_OF_BIRTH]: toDateInput(c.dateOfBirth),
            [ContractorInputs.STREET_1]: c.addressLine1 || '',
            [ContractorInputs.CITY]: c.city || '',
            [ContractorInputs.STATE]: c.state || '',
            [ContractorInputs.ZIP_CODE]: c.zip || '',
            [ContractorInputs.HOME_PHONE]: c.homePhone || '',
            [ContractorInputs.CELL_PHONE]: c.cellPhone || '',
            [ContractorInputs.WAGE]: c.payRate ? String(c.payRate) : '',
            [ContractorInputs.WAGE_PERIOD]: c.payType || '',
            // Business info
            [ContractorInputs.BUSINESS_NAME]: c.businessName || '',
            [ContractorInputs.TRADE_NAME]: c.tradeName || '',
            [ContractorInputs.SERVICE_TYPE]: c.serviceType || '',
            [ContractorInputs.BUSINESS_ADDRESS]: c.businessAddress || '',
            // Emergency contact
            [ContractorInputs.EMERGENCY_FIRST_NAME]: c.emergencyFirstName || '',
            [ContractorInputs.EMERGENCY_LAST_NAME]: c.emergencyLastName || '',
            [ContractorInputs.EMERGENCY_RELATIONSHIP]: c.emergencyRelationship || '',
            [ContractorInputs.EMERGENCY_PHONE]: c.emergencyPhone || '',
            [ContractorInputs.EMERGENCY_ALT_PHONE]: c.emergencyAltPhone || '',
            [ContractorInputs.EMERGENCY_EMAIL]: c.emergencyEmail || '',
        });
    }, [contractorData, prefillData, reset]);

    const updateBankAccount = (idx, field, value) =>
        setBankAccounts((prev) => prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
    const addBankAccount = () => {
        setBankAccounts((prev) => [...prev, emptyBankAccount()]);
        setAddModeSplits((prev) =>
            prev.length === 0
                ? [{ splitType: "percentage", value: "50" }, { splitType: "remaining_balance", value: null }]
                : [...prev, { splitType: "remaining_balance", value: null }]
        );
    };
    const removeBankAccount = (idx) => {
        setBankAccounts((prev) => prev.filter((_, i) => i !== idx));
        setAddModeSplits((prev) => prev.filter((_, i) => i !== idx));
    };
    const updateAddModeSplit = (idx, field, value) =>
        setAddModeSplits((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));

    const handleAddExistingBankAccount = async () => {
        if (!newBankAccount.accountNumber || !newBankAccount.routingNumber) return;
        try {
            await createContractorBankAccount(contractorId, {
                accountType: newBankAccount.accountType,
                accountNumber: newBankAccount.accountNumber,
                routingNumber: newBankAccount.routingNumber,
            });
            setNewBankAccount(emptyBankAccount());
            setShowAddBankForm(false);
            refetchBankAccounts();
        } catch {
            toast("Failed to add bank account.", 'error');
        }
    };

    const onSubmit = useCallback(
        async (values) => {
            const primaryWorkplaceId = values[ContractorInputs.PRIMARY_WORKPLACE_ID] || null;
            const selectedDeptId = values[ContractorInputs.DEPARTMENT_ID] || null;
            const deptName = departmentOptions.find(d => d.value === selectedDeptId)?.label || null;
            const wpName   = workplaceOptions.find(w => w.value === primaryWorkplaceId)?.label || null;
            const payload = {
                firstName: values[ContractorInputs.FIRSTNAME],
                middleName: values[ContractorInputs.MIDDLENAME] || null,
                lastName: values[ContractorInputs.LASTNAME],
                email: values[ContractorInputs.CONTACT_EMAIL],
                startDate: values[ContractorInputs.CONTRACT_START_DATE] || null,
                type: values[ContractorInputs.CONTRACTOR_TYPE],
                defaultPaymentMethod: values[ContractorInputs.PAYMENT_METHOD],
                departmentId: selectedDeptId,
                department: deptName,
                primaryWorkplaceId,
                workLocation: wpName,
                workplaces: primaryWorkplaceId ? [primaryWorkplaceId] : [],
                locationId: values[ContractorInputs.LOCATION_ID] || null,
                form1099ElectronicConsent: false,
                active: true,
                workFromHome: values[ContractorInputs.IS_WORK_FROM_HOME] || null,
                ssn: values[ContractorInputs.SSN] || null,
                ein: values[ContractorInputs.EIN] || null,
                dateOfBirth: values[ContractorInputs.DATE_OF_BIRTH] || null,
                street1: values[ContractorInputs.STREET_1] || null,
                city: values[ContractorInputs.CITY] || null,
                state: values[ContractorInputs.STATE] || null,
                zipcode: values[ContractorInputs.ZIP_CODE] || null,
                homePhone: values[ContractorInputs.HOME_PHONE] || null,
                cellPhone: values[ContractorInputs.CELL_PHONE] || null,
                payRate: parseFloat(values[ContractorInputs.WAGE]) || null,
                payType: values[ContractorInputs.WAGE_PERIOD] || null,
                // Business info
                businessName: values[ContractorInputs.BUSINESS_NAME] || null,
                tradeName: values[ContractorInputs.TRADE_NAME] || null,
                serviceType: values[ContractorInputs.SERVICE_TYPE] || null,
                businessAddress: values[ContractorInputs.BUSINESS_ADDRESS] || null,
                // Emergency contact
                emergencyFirstName: values[ContractorInputs.EMERGENCY_FIRST_NAME] || null,
                emergencyLastName: values[ContractorInputs.EMERGENCY_LAST_NAME] || null,
                emergencyRelationship: values[ContractorInputs.EMERGENCY_RELATIONSHIP] || null,
                emergencyPhone: values[ContractorInputs.EMERGENCY_PHONE] || null,
                emergencyAltPhone: values[ContractorInputs.EMERGENCY_ALT_PHONE] || null,
                emergencyEmail: values[ContractorInputs.EMERGENCY_EMAIL] || null,
            };
            if (values[ContractorInputs.TERMINATION_DATE]) payload.terminationDate = values[ContractorInputs.TERMINATION_DATE];

            if (isEditMode) {
                mutateUpdate({ contractorId, ...payload });
                return;
            }

            if (paymentMethod === "DirectDeposit" && bankAccounts.length >= 2 && addModeSplits.length >= 2) {
                const remCount = addModeSplits.filter((s) => s.splitType === "remaining_balance").length;
                const totalPct = addModeSplits.filter((s) => s.splitType === "percentage").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                if (remCount > 1) {
                    setSubmitError("Only one split can be Remaining Balance.");
                    return;
                }
                if (remCount === 0 && Math.abs(totalPct - 100) > 0.01) {
                    setSubmitError("Percentages must total 100%. Current total: " + totalPct.toFixed(1) + "%.");
                    return;
                }
                if (remCount > 0 && totalPct >= 100) {
                    setSubmitError("Percentage splits total " + totalPct.toFixed(1) + "% — leave room for Remaining Balance.");
                    return;
                }
            }

            setIsSubmitting(true);
            setSubmitError(null);
            try {
                const newContractor = await createContractor(payload);
                const newId = newContractor?.id || newContractor?.contractorId;
                const createdBankIds = [];
                if (paymentMethod === "DirectDeposit" && newId) {
                    for (const acct of bankAccounts) {
                        if (acct.accountNumber && acct.routingNumber) {
                            const created = await createContractorBankAccount(newId, {
                                accountType: acct.accountType || "checking",
                                accountNumber: acct.accountNumber,
                                routingNumber: acct.routingNumber,
                            });
                            createdBankIds.push(created?.id || created?.bankAccountId);
                        }
                    }
                    if (createdBankIds.length >= 2 && addModeSplits.length >= 2) {
                        const splitsToSave = addModeSplits.slice(0, createdBankIds.length).map((s, i) => ({
                            bankAccountId: createdBankIds[i],
                            splitType: s.splitType || "remaining_balance",
                            value: s.value,
                        }));
                        await createContractorNetPaySplit(newId, splitsToSave);
                    }
                }
                queryClient.invalidateQueries({ queryKey: ["contractors"] });
                if (sendInvite && newId) {
                    try {
                        const inv = await inviteContractor(newId);
                        setInviteResult(inv?.alreadyExists ? 'exists' : 'sent');
                    } catch {
                        setInviteResult('error');
                    }
                }
                const isPendingOnboarding = fromOnboarding ||
                    contractorData?.onboardingStatus === "pending_review" ||
                    contractorData?.onboardStatus === "pending_review" ||
                    prefillData?.onboardingStatus === "pending_review" ||
                    prefillData?.onboardStatus === "pending_review";
                navigate(isPendingOnboarding ? "/onboarding" : "/contractor");
            } catch (err) {
                setSubmitError(err?.response?.data?.message || "Failed to save contractor. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        },
        [mutateUpdate, isEditMode, contractorId, paymentMethod, bankAccounts, addModeSplits, queryClient, navigate, sendInvite]
    );

    const isPending = isCreating || isUpdating;
    const isError = isCreateError || isUpdateError;
    const error = createError || updateError;

    const watchedWage = watch(ContractorInputs.WAGE);
    const watchedPeriod = watch(ContractorInputs.WAGE_PERIOD);
    const wageEmpty = highlightSetup && (!watchedWage || Number(watchedWage) === 0);
    const periodEmpty = highlightSetup && !watchedPeriod;

    return (
        <form className={styles["add-edit-contractor-container"]} onSubmit={handleSubmit(onSubmit)}>
            <h3 className={styles["title"]}>{isEditMode ? 'Edit Contractor' : 'Add Contractor'}</h3>

            {/* Pending onboarding banner — shown when editing a pending_review contractor */}
            {isEditMode && (contractorData?.onboardingStatus === "pending_review" || contractorData?.onboardStatus === "pending_review" || prefillData?.onboardingStatus === "pending_review" || prefillData?.onboardStatus === "pending_review") && (
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, background:'#EFF6FF', border:'2px solid #1B75F0', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
                    <span style={{ fontSize:20 }}>👤</span>
                    <div>
                        <div style={{ fontWeight:700, color:'#c2410c', fontSize:14 }}>Pending Onboarding — Review &amp; Save</div>
                        <div style={{ fontSize:13, color:'#7c2d12', marginTop:2 }}>
                            This contractor is awaiting onboarding approval. Fill in any missing details, then click <strong>Update Contractor</strong>.
                            You will be returned to the <strong>Pending Onboardings</strong> page to approve them.
                        </div>
                    </div>
                </div>
            )}

            {/* Setup completion banner */}
            {highlightSetup && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: '#EFF6FF', border: '2px solid #1B75F0',
                    borderRadius: 10, padding: '12px 18px', marginBottom: 20,
                }}>
                    <span style={{ fontSize: 20 }}>⚙️</span>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#c2410c', fontSize: 13, marginBottom: 2 }}>
                            Complete contractor setup to enable payroll
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 4 }}>
                            {[
                                { label: 'Wage / Pay rate', ok: !wageEmpty },
                                { label: 'Pay period / Type', ok: !periodEmpty },
                            ].map(({ label, ok }) => (
                                <span key={label} style={{
                                    fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
                                    border: '1.5px solid', borderColor: ok ? '#16a34a' : '#1B75F0',
                                    color: ok ? '#15803d' : '#c2410c', background: ok ? '#f0fdf4' : '#EFF6FF',
                                }}>
                                    {ok ? '✓' : '○'} {label}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className={styles["personal-info-container"]}>
                <p className={styles["info-title"]}>Personal Information</p>
                <div className={cn(styles["personal-info-form"], "")}>
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="First Name" required variant="flat"
                                {...register(ContractorInputs.FIRSTNAME)}
                                error={!!errors[ContractorInputs.FIRSTNAME]}
                                errorText={errors[ContractorInputs.FIRSTNAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Middle Name" variant="flat"
                                {...register(ContractorInputs.MIDDLENAME)}
                                error={!!errors[ContractorInputs.MIDDLENAME]}
                                errorText={errors[ContractorInputs.MIDDLENAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Last Name" required variant="flat"
                                {...register(ContractorInputs.LASTNAME)}
                                error={!!errors[ContractorInputs.LASTNAME]}
                                errorText={errors[ContractorInputs.LASTNAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Contact Email" required variant="flat"
                                {...register(ContractorInputs.CONTACT_EMAIL)}
                                error={!!errors[ContractorInputs.CONTACT_EMAIL]}
                                errorText={errors[ContractorInputs.CONTACT_EMAIL]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Date of Birth" required variant="flat" type="date"
                                {...register(ContractorInputs.DATE_OF_BIRTH)}
                                error={!!errors[ContractorInputs.DATE_OF_BIRTH]}
                                errorText={errors[ContractorInputs.DATE_OF_BIRTH]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Contract Start Date" required variant="flat" type="date"
                                {...register(ContractorInputs.CONTRACT_START_DATE)}
                                error={!!errors[ContractorInputs.CONTRACT_START_DATE]}
                                errorText={errors[ContractorInputs.CONTRACT_START_DATE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Termination Date" variant="flat" type="date"
                                {...register(ContractorInputs.TERMINATION_DATE)}
                                error={!!errors[ContractorInputs.TERMINATION_DATE]}
                                errorText={errors[ContractorInputs.TERMINATION_DATE]?.message} />
                        </div>

                        {/* Contractor Type drives which ID field shows below */}
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Contractor Type" required placeholder="Select type" options={CONTRACTOR_TYPES}
                                {...register(ContractorInputs.CONTRACTOR_TYPE)}
                                error={!!errors[ContractorInputs.CONTRACTOR_TYPE]}
                                errorText={errors[ContractorInputs.CONTRACTOR_TYPE]?.message} />
                        </div>

                        {/* SSN — shown for Individual */}
                        {isIndividualType && (
                            <div className="col-lg-4 col-md-6 col-12">
                                <Input fullWidth label="SSN" required variant="flat" placeholder="xxx-xx-xxxx"
                                    {...register(ContractorInputs.SSN)}
                                    error={!!errors[ContractorInputs.SSN]}
                                    errorText={errors[ContractorInputs.SSN]?.message} />
                            </div>
                        )}

                        {/* EIN — shown for Business */}
                        {isBusinessType && (
                            <div className="col-lg-4 col-md-6 col-12">
                                <Input fullWidth label="EIN (xx-xxxxxxx)" required variant="flat" placeholder="xx-xxxxxxx"
                                    {...register(ContractorInputs.EIN)}
                                    onChange={e => {
                                        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                                        const formatted = digits.length > 2 ? digits.slice(0, 2) + '-' + digits.slice(2) : digits;
                                        setValue(ContractorInputs.EIN, formatted, { shouldValidate: true });
                                    }}
                                    maxLength={10}
                                    error={!!errors[ContractorInputs.EIN]}
                                    errorText={errors[ContractorInputs.EIN]?.message} />
                            </div>
                        )}
                    </div>
                    <div className="row g-3 mt-2">
                        <div className="col-lg-4 col-md-6 col-12">
                            <AddressAutocomplete
                                label="Street Address"
                                required
                                value={watch(ContractorInputs.STREET_1) || ''}
                                onSelect={(a) => {
                                    setValue(ContractorInputs.STREET_1, a.line1, { shouldValidate: true });
                                    setValue(ContractorInputs.CITY,     a.city,  { shouldValidate: true });
                                    setValue(ContractorInputs.STATE,    a.state, { shouldValidate: true });
                                    setValue(ContractorInputs.ZIP_CODE, a.zip,   { shouldValidate: true });
                                }}
                                onChange={(v) => setValue(ContractorInputs.STREET_1, v, { shouldValidate: true })}
                                error={!!errors[ContractorInputs.STREET_1]}
                                errorText={errors[ContractorInputs.STREET_1]?.message}
                            />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="City" required variant="flat"
                                {...register(ContractorInputs.CITY)}
                                error={!!errors[ContractorInputs.CITY]}
                                errorText={errors[ContractorInputs.CITY]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="State" required placeholder="Select state" options={US_STATES}
                                {...register(ContractorInputs.STATE)}
                                error={!!errors[ContractorInputs.STATE]}
                                errorText={errors[ContractorInputs.STATE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Zip Code" required variant="flat"
                                {...register(ContractorInputs.ZIP_CODE)}
                                error={!!errors[ContractorInputs.ZIP_CODE]}
                                errorText={errors[ContractorInputs.ZIP_CODE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Home Phone" variant="flat"
                                {...register(ContractorInputs.HOME_PHONE)}
                                onChange={e => {
                                    const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    const fmt = d.length <= 3 ? (d.length ? `(${d}` : '') : d.length <= 6 ? `(${d.slice(0,3)}) ${d.slice(3)}` : `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                                    setValue(ContractorInputs.HOME_PHONE, fmt, { shouldValidate: true });
                                }}
                                maxLength={14}
                                placeholder="(555) 555-5555"
                                error={!!errors[ContractorInputs.HOME_PHONE]}
                                errorText={errors[ContractorInputs.HOME_PHONE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Cell Phone" required variant="flat"
                                {...register(ContractorInputs.CELL_PHONE)}
                                onChange={e => {
                                    const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    const fmt = d.length <= 3 ? (d.length ? `(${d}` : '') : d.length <= 6 ? `(${d.slice(0,3)}) ${d.slice(3)}` : `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                                    setValue(ContractorInputs.CELL_PHONE, fmt, { shouldValidate: true });
                                }}
                                maxLength={14}
                                placeholder="(555) 555-5555"
                                error={!!errors[ContractorInputs.CELL_PHONE]}
                                errorText={errors[ContractorInputs.CELL_PHONE]?.message} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Business Info — only for Business contractor type */}
            {isBusinessType && (
                <div className={cn(styles["personal-info-container"], "mt-4")}>
                    <p className={styles["info-title"]}>Business Information</p>
                    <div className={cn(styles["personal-info-form"], "")}>
                        <div className="row g-3">
                            <div className="col-lg-4 col-md-6 col-12">
                                <Input fullWidth label="Business / Legal Name" variant="flat" placeholder="Legal registered name"
                                    {...register(ContractorInputs.BUSINESS_NAME)}
                                    error={!!errors[ContractorInputs.BUSINESS_NAME]}
                                    errorText={errors[ContractorInputs.BUSINESS_NAME]?.message} />
                            </div>
                            <div className="col-lg-4 col-md-6 col-12">
                                <Input fullWidth label="Trade Name (DBA)" variant="flat" placeholder="Doing business as (optional)"
                                    {...register(ContractorInputs.TRADE_NAME)}
                                    error={!!errors[ContractorInputs.TRADE_NAME]}
                                    errorText={errors[ContractorInputs.TRADE_NAME]?.message} />
                            </div>
                            <div className="col-lg-4 col-md-6 col-12">
                                <Select fullWidth label="Service / Work Type" placeholder="Select service type" options={SERVICE_TYPES}
                                    {...register(ContractorInputs.SERVICE_TYPE)}
                                    error={!!errors[ContractorInputs.SERVICE_TYPE]}
                                    errorText={errors[ContractorInputs.SERVICE_TYPE]?.message} />
                            </div>
                            <div className="col-lg-8 col-md-12 col-12">
                                <Input fullWidth label="Business Address" variant="flat" placeholder="Business street address"
                                    {...register(ContractorInputs.BUSINESS_ADDRESS)}
                                    error={!!errors[ContractorInputs.BUSINESS_ADDRESS]}
                                    errorText={errors[ContractorInputs.BUSINESS_ADDRESS]?.message} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className={cn(styles["contract-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Work Assignment</p>
                <div className={cn(styles["contract-info-form"], "container-fluid")}>
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Primary Workplace" placeholder="Select workplace" options={workplaceOptions}
                                {...register(ContractorInputs.PRIMARY_WORKPLACE_ID)}
                                error={!!errors[ContractorInputs.PRIMARY_WORKPLACE_ID]}
                                errorText={errors[ContractorInputs.PRIMARY_WORKPLACE_ID]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Department" placeholder="Select department" options={departmentOptions}
                                {...register(ContractorInputs.DEPARTMENT_ID)}
                                error={!!errors[ContractorInputs.DEPARTMENT_ID]}
                                errorText={errors[ContractorInputs.DEPARTMENT_ID]?.message} />
                        </div>
                        {locationOptions.length > 1 && (
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Branch / Location"
                                placeholder="Assign to location" options={locationOptions}
                                value={watch(ContractorInputs.LOCATION_ID)}
                                {...register(ContractorInputs.LOCATION_ID)}
                                error={!!errors[ContractorInputs.LOCATION_ID]}
                                errorText={errors[ContractorInputs.LOCATION_ID]?.message} />
                        </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={cn(styles["work-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Work Information</p>
                <div className={cn(styles["work-info-form"], "container-fluid")}>
                    <h4 className={styles["label"]}>Primarily Work From Home</h4>
                    <div className={styles["radio-group"]}>
                        <Controller
                            name={ContractorInputs.IS_WORK_FROM_HOME}
                            control={control}
                            defaultValue={contractorData?.workFromHome || ""}
                            render={({ field }) => (
                                <>
                                    <Radio {...field} value="yes" checked={field.value === "yes"} label="Yes" />
                                    <Radio {...field} value="no" checked={field.value === "no"} label="No" />
                                </>
                            )}
                        />
                    </div>
                </div>
            </div>

            <div ref={wageRef} className={cn(styles["work-info-container"], "mt-4")} style={highlightSetup && (wageEmpty || periodEmpty) ? { border: '2px solid #1B75F0', borderRadius: 10, padding: 16, background: '#EFF6FF' } : {}}>
                <p className={styles["info-title"]} style={highlightSetup && (wageEmpty || periodEmpty) ? { color: '#c2410c' } : {}}>
                    Wage Information {highlightSetup && (wageEmpty || periodEmpty) && <span style={{ fontSize: 12, fontWeight: 600, background: '#1B75F0', color: '#fff', borderRadius: 20, padding: '2px 8px', marginLeft: 8 }}>Action Required</span>}
                </p>
                <div className={cn(styles["work-info-form"], "container-fluid")}>
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Wage" required variant="flat" type="number"
                                {...register(ContractorInputs.WAGE)}
                                error={!!errors[ContractorInputs.WAGE] || wageEmpty}
                                errorText={wageEmpty ? 'Pay rate is required to run payroll' : errors[ContractorInputs.WAGE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Period" required placeholder="Select pay type" options={PAY_TYPES}
                                {...register(ContractorInputs.WAGE_PERIOD)}
                                error={!!errors[ContractorInputs.WAGE_PERIOD] || periodEmpty}
                                errorText={periodEmpty ? 'Pay type is required to run payroll' : errors[ContractorInputs.WAGE_PERIOD]?.message} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Emergency Contact */}
            <div className={cn(styles["personal-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Emergency Contact</p>
                <div className={cn(styles["personal-info-form"], "")}>
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="First Name" variant="flat"
                                {...register(ContractorInputs.EMERGENCY_FIRST_NAME)}
                                error={!!errors[ContractorInputs.EMERGENCY_FIRST_NAME]}
                                errorText={errors[ContractorInputs.EMERGENCY_FIRST_NAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Last Name" variant="flat"
                                {...register(ContractorInputs.EMERGENCY_LAST_NAME)}
                                error={!!errors[ContractorInputs.EMERGENCY_LAST_NAME]}
                                errorText={errors[ContractorInputs.EMERGENCY_LAST_NAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Relationship" variant="flat" placeholder="e.g. Spouse, Parent, Friend"
                                {...register(ContractorInputs.EMERGENCY_RELATIONSHIP)}
                                error={!!errors[ContractorInputs.EMERGENCY_RELATIONSHIP]}
                                errorText={errors[ContractorInputs.EMERGENCY_RELATIONSHIP]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Primary Phone" variant="flat" placeholder="(555) 555-5555"
                                {...register(ContractorInputs.EMERGENCY_PHONE)}
                                onChange={e => {
                                    const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    const fmt = d.length <= 3 ? (d.length ? `(${d}` : '') : d.length <= 6 ? `(${d.slice(0,3)}) ${d.slice(3)}` : `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                                    setValue(ContractorInputs.EMERGENCY_PHONE, fmt, { shouldValidate: true });
                                }}
                                maxLength={14}
                                error={!!errors[ContractorInputs.EMERGENCY_PHONE]}
                                errorText={errors[ContractorInputs.EMERGENCY_PHONE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Alternate Phone" variant="flat" placeholder="(555) 555-5555 (optional)"
                                {...register(ContractorInputs.EMERGENCY_ALT_PHONE)}
                                onChange={e => {
                                    const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    const fmt = d.length <= 3 ? (d.length ? `(${d}` : '') : d.length <= 6 ? `(${d.slice(0,3)}) ${d.slice(3)}` : `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                                    setValue(ContractorInputs.EMERGENCY_ALT_PHONE, fmt, { shouldValidate: true });
                                }}
                                maxLength={14}
                                error={!!errors[ContractorInputs.EMERGENCY_ALT_PHONE]}
                                errorText={errors[ContractorInputs.EMERGENCY_ALT_PHONE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Email" variant="flat" placeholder="emergency@example.com (optional)"
                                {...register(ContractorInputs.EMERGENCY_EMAIL)}
                                error={!!errors[ContractorInputs.EMERGENCY_EMAIL]}
                                errorText={errors[ContractorInputs.EMERGENCY_EMAIL]?.message} />
                        </div>
                    </div>
                </div>
            </div>

            <div className={cn(styles["work-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Payment Method</p>
                <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
                    Contractors with a bank account on file are paid via <strong>Direct Deposit</strong>. Without a bank account they are paid by <strong>Check</strong>.
                </p>
                {isEditMode ? (
                    <div>
                        {loadingBankAccounts && <p style={{ fontSize: 13, color: "#888" }}>Loading payment info...</p>}
                        {existingBankAccounts.map((acct) => (
                            <div key={acct.id} className="p-3 mb-3" style={{ border: "1px solid #dee2e6", borderRadius: 8, background: "#fafafa" }}>
                                {editingBankAccountId === acct.id ? (
                                    <div>
                                        <strong style={{ fontSize: 14 }}>Edit Bank Account</strong>
                                        <div className="row g-3 mt-1">
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Select fullWidth label="Account Type"
                                                    options={[{ label: "Checking", value: "checking" }, { label: "Savings", value: "savings" }]}
                                                    value={editBankAccountData.accountType ?? acct.accountType ?? "checking"}
                                                    onChange={(e) => setEditBankAccountData((p) => ({ ...p, accountType: e.target.value }))} />
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Input fullWidth label="Account Number" variant="flat"
                                                    value={editBankAccountData.accountNumber ?? acct.accountNumber ?? ""}
                                                    onChange={(e) => setEditBankAccountData((p) => ({ ...p, accountNumber: e.target.value }))} />
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Input fullWidth label="Routing Number" variant="flat"
                                                    value={editBankAccountData.routingNumber ?? acct.routingNumber ?? ""}
                                                    onChange={(e) => setEditBankAccountData((p) => ({ ...p, routingNumber: e.target.value }))} />
                                            </div>
                                        </div>
                                        <div className="mt-3 d-flex gap-2">
                                            <button type="button" className="btn btn-sm" style={{ backgroundColor: "#1B75F0", color: "#fff" }}
                                                onClick={async () => {
                                                    try {
                                                        await updateContractorBankAccount(contractorId, acct.id, editBankAccountData);
                                                        setEditingBankAccountId(null);
                                                        setEditBankAccountData({});
                                                        refetchBankAccounts();
                                                    } catch {
                                                        toast("Failed to update account.", 'error');
                                                    }
                                                }}>Save</button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary"
                                                onClick={() => { setEditingBankAccountId(null); setEditBankAccountData({}); }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <span className="fw-semibold" style={{ fontSize: 13 }}>Bank Account</span>
                                            <span className="ms-2 badge" style={{ background: "#d4edda", color: "#155724", fontSize: 11 }}>Direct Deposit</span>
                                            <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
                                                {(acct.accountType || "savings").charAt(0).toUpperCase() + (acct.accountType || "savings").slice(1)} — {maskAccount(acct.accountNumber)} &nbsp; Routing: {acct.routingNumber}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button type="button" className="btn btn-sm btn-outline-secondary"
                                                onClick={() => { setEditingBankAccountId(acct.id); setEditBankAccountData({ accountType: acct.accountType, accountNumber: acct.accountNumber, routingNumber: acct.routingNumber }); }}>Edit</button>
                                            {existingBankAccounts.length > 1 && (
                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setConfirmRemoveId(acct.id)}>Remove</button>
                                            )}
                                            {confirmRemoveId === acct.id && (
                                                <div className="mt-2 p-2" style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 6, fontSize: 13 }}>
                                                    <p className="mb-2 fw-semibold" style={{ color: "#856404" }}>Remove this bank account?</p>
                                                    <div className="d-flex gap-2">
                                                        <button type="button" className="btn btn-sm btn-danger"
                                                            onClick={async () => {
                                                                try {
                                                                    await deleteContractorBankAccount(contractorId, acct.id);
                                                                    if (existingBankAccounts.length <= 2) setSplits([]);
                                                                    setConfirmRemoveId(null);
                                                                    refetchBankAccounts();
                                                                } catch {
                                                                    setConfirmRemoveId(null);
                                                                }
                                                            }}>Yes, Remove</button>
                                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmRemoveId(null)}>Cancel</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {!loadingBankAccounts && existingBankAccounts.length === 0 && (
                            <p style={{ fontSize: 13, color: "#888" }}>No bank account on file — contractor will be paid by <strong>Check</strong>.</p>
                        )}
                        {showAddBankForm ? (
                            <div className="p-3 mt-3" style={{ border: "1px solid #dee2e6", borderRadius: 8, background: "#fafafa" }}>
                                <strong style={{ fontSize: 14 }}>New Bank Account</strong>
                                <div className="row g-3 mt-1">
                                    <div className="col-lg-4 col-md-6 col-12">
                                        <Select fullWidth label="Account Type"
                                            options={[{ label: "Checking", value: "checking" }, { label: "Savings", value: "savings" }]}
                                            value={newBankAccount.accountType}
                                            onChange={(e) => setNewBankAccount((p) => ({ ...p, accountType: e.target.value }))} />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-12">
                                        <Input fullWidth label="Account Number" variant="flat"
                                            value={newBankAccount.accountNumber}
                                            onChange={(e) => setNewBankAccount((p) => ({ ...p, accountNumber: e.target.value }))} />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-12">
                                        <Input fullWidth label="Routing Number" variant="flat"
                                            value={newBankAccount.routingNumber}
                                            onChange={(e) => setNewBankAccount((p) => ({ ...p, routingNumber: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mt-3 d-flex gap-2">
                                    <button type="button" className="btn btn-sm" style={{ backgroundColor: "#1B75F0", color: "#fff" }} onClick={handleAddExistingBankAccount}>Save Account</button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary"
                                        onClick={() => { setShowAddBankForm(false); setNewBankAccount(emptyBankAccount()); }}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex gap-2 mt-3 align-items-center flex-wrap">
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#1B75F0", color: "#fff", borderRadius: 6 }}
                                    onClick={() => setShowAddBankForm(true)}>+ Add Bank Account</button>
                                {existingBankAccounts.length > 0 && !showSwitchToCheckConfirm && (
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setShowSwitchToCheckConfirm(true)}>Switch to Check</button>
                                )}
                                {showSwitchToCheckConfirm && (
                                    <div className="mt-2 p-3" style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, fontSize: 13 }}>
                                        <p className="mb-2 fw-semibold" style={{ color: "#856404" }}>Switch to Check Payment?</p>
                                        <p className="mb-3" style={{ color: "#856404" }}>This will remove all bank accounts. The contractor will be paid by check.</p>
                                        <div className="d-flex gap-2">
                                            <button type="button" className="btn btn-sm btn-danger"
                                                onClick={async () => {
                                                    try {
                                                        for (const acct of existingBankAccounts) await deleteContractorBankAccount(contractorId, acct.id);
                                                        setSplits([]);
                                                        setShowSwitchToCheckConfirm(false);
                                                        refetchBankAccounts();
                                                    } catch {
                                                        setShowSwitchToCheckConfirm(false);
                                                    }
                                                }}>Yes, Switch to Check</button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowSwitchToCheckConfirm(false)}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {existingBankAccounts.length > 1 && (
                            <div className="mt-4">
                                <p className={styles["info-title"]} style={{ fontSize: 14 }}>Direct Deposit Allocation</p>
                                <table className="table table-sm" style={{ fontSize: 13 }}>
                                    <thead><tr><th>Account</th><th>Split Type</th><th>Value</th><th></th></tr></thead>
                                    <tbody>
                                        {splits.map((split, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <select className="form-select form-select-sm" value={split.bankAccountId || ""}
                                                        onChange={(e) => setSplits((prev) => prev.map((s, i) => (i === idx ? { ...s, bankAccountId: e.target.value } : s)))}>
                                                        <option value="">Select account</option>
                                                        {existingBankAccounts.map((a) => (
                                                            <option key={a.id} value={a.id}>{(a.accountType || "Account").charAt(0).toUpperCase() + (a.accountType || "account").slice(1)} ****{String(a.accountNumber || "").slice(-4)}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select className="form-select form-select-sm" value={split.splitType || "remaining_balance"}
                                                        onChange={(e) => {
                                                            const newType = e.target.value;
                                                            setSplits((prev) => prev.map((s, i) => (i === idx ? { ...s, splitType: newType, value: newType === "remaining_balance" ? null : (s.value ?? null) } : s)));
                                                        }}>
                                                        <option value="remaining_balance">Remaining Balance</option>
                                                        <option value="percentage">Percentage (%)</option>
                                                        <option value="flat">Flat Amount ($)</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    {split.splitType !== "remaining_balance" ? (
                                                        <input type="number" className="form-control form-control-sm"
                                                            placeholder={split.splitType === "percentage" ? "e.g. 50" : "e.g. 500"}
                                                            value={split.value ?? ""}
                                                            onChange={(e) => setSplits((prev) => prev.map((s, i) => (i === idx ? { ...s, value: e.target.value } : s)))} />
                                                    ) : (() => {
                                                        const usedPct = splits.filter((s, i) => i !== idx && s.splitType === "percentage").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                                                        return <input type="text" className="form-control form-control-sm" readOnly value={Math.round(Math.max(0, 100 - usedPct))} style={{ background: "#f0f9f0", color: "#155724", fontWeight: 600, cursor: "default" }} />;
                                                    })()}
                                                </td>
                                                <td><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setSplits((prev) => prev.filter((_, i) => i !== idx))}>Remove</button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setSplits((prev) => [...prev, { bankAccountId: "", splitType: "percentage", value: null }])}>+ Add Split</button>
                                <button type="button" className="btn btn-sm ms-2" style={{ backgroundColor: "#1B75F0", color: "#fff" }}
                                    onClick={async () => {
                                        if (splits.some((s) => !s.bankAccountId)) { setSplitMessage("error:Please select a bank account for each split."); return; }
                                        const remCount = splits.filter((s) => s.splitType === "remaining_balance").length;
                                        if (remCount > 1) { setSplitMessage("error:Only one split can be Remaining Balance."); return; }
                                        const totalPct = splits.filter((s) => s.splitType === "percentage").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                                        if (remCount === 0 && splits.every((s) => s.splitType === "percentage") && Math.abs(totalPct - 100) > 0.01) { setSplitMessage("error:Percentages must total 100%."); return; }
                                        if (remCount > 0 && totalPct >= 100) { setSplitMessage("error:Leave room for Remaining Balance."); return; }
                                        try {
                                            await createContractorNetPaySplit(contractorId, splits);
                                            setSplitMessage("Allocations saved.");
                                            setTimeout(() => setSplitMessage(""), 3000);
                                            await refetchSplits();
                                        } catch {
                                            setSplitMessage("error:Failed to save allocations.");
                                        }
                                    }}>Save Allocations</button>
                                {splitMessage && (
                                    <p className="mt-2" style={{ fontSize: 13, color: splitMessage.startsWith("error:") ? "#dc3545" : "#198754" }}>
                                        {splitMessage.startsWith("error:") ? splitMessage.slice(6) : splitMessage}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <div className="row g-3">
                            <div className="col-lg-4 col-md-6 col-12">
                                <Select fullWidth label="Payment Method" required placeholder="Select payment method" options={PAYMENT_METHODS}
                                    {...register(ContractorInputs.PAYMENT_METHOD)}
                                    error={!!errors[ContractorInputs.PAYMENT_METHOD]}
                                    errorText={errors[ContractorInputs.PAYMENT_METHOD]?.message} />
                            </div>
                        </div>
                        {paymentMethod === "DirectDeposit" && (
                            <div className="mt-3">
                                {bankAccounts.map((acct, idx) => (
                                    <div key={idx} className="p-3 mb-3" style={{ border: "1px solid #dee2e6", borderRadius: 8, background: "#fafafa" }}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <strong style={{ fontSize: 14 }}>Bank Account {idx + 1}</strong>
                                            {idx > 0 && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeBankAccount(idx)}>Remove</button>}
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Select fullWidth label="Account Type"
                                                    options={[{ label: "Checking", value: "checking" }, { label: "Savings", value: "savings" }]}
                                                    value={acct.accountType}
                                                    onChange={(e) => updateBankAccount(idx, "accountType", e.target.value)} />
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Input fullWidth label="Account Number" variant="flat"
                                                    value={acct.accountNumber}
                                                    onChange={(e) => updateBankAccount(idx, "accountNumber", e.target.value)} />
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Input fullWidth label="Routing Number" variant="flat"
                                                    value={acct.routingNumber}
                                                    onChange={(e) => updateBankAccount(idx, "routingNumber", e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#1B75F0", color: "#fff", borderRadius: 6 }} onClick={addBankAccount}>+ Add Another Bank Account</button>
                                {bankAccounts.length >= 2 && (
                                    <div className="mt-4">
                                        <p className={styles["info-title"]} style={{ fontSize: 14 }}>Direct Deposit Allocation</p>
                                        <p style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>Allocate net pay across accounts by percentage or flat amount.</p>
                                        <table className="table table-sm" style={{ fontSize: 13 }}>
                                            <thead><tr><th>Account</th><th>Split Type</th><th>Value</th><th></th></tr></thead>
                                            <tbody>
                                                {addModeSplits.map((split, idx) => (
                                                    <tr key={idx}>
                                                        <td style={{ verticalAlign: "middle" }}>
                                                            <span style={{ fontSize: 13 }}>
                                                                {(bankAccounts[idx]?.accountType || "Account").charAt(0).toUpperCase() + (bankAccounts[idx]?.accountType || "account").slice(1)} {bankAccounts[idx]?.accountNumber ? "****" + String(bankAccounts[idx].accountNumber).slice(-4) : "****"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <select className="form-select form-select-sm" value={split.splitType || "remaining_balance"}
                                                                onChange={(e) => updateAddModeSplit(idx, "splitType", e.target.value)}>
                                                                <option value="remaining_balance">Remaining Balance</option>
                                                                <option value="percentage">Percentage (%)</option>
                                                                <option value="flat">Flat Amount ($)</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            {split.splitType !== "remaining_balance" ? (
                                                                <input type="number" className="form-control form-control-sm"
                                                                    placeholder={split.splitType === "percentage" ? "e.g. 50" : "e.g. 500"}
                                                                    value={split.value ?? ""}
                                                                    onChange={(e) => updateAddModeSplit(idx, "value", e.target.value)} />
                                                            ) : (() => {
                                                                const usedPct = addModeSplits.filter((s, i) => i !== idx && s.splitType === "percentage").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                                                                const rem = Math.max(0, 100 - usedPct);
                                                                return <input type="text" className="form-control form-control-sm" readOnly value={Math.round(rem)} style={{ background: "#f0f9f0", color: "#155724", fontWeight: 600, cursor: "default", width: 60 }} />;
                                                            })()}
                                                        </td>
                                                        <td>{bankAccounts.length > 2 && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeBankAccount(idx)}>Remove</button>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                        {paymentMethod === "Check" && (
                            <p className="mt-2" style={{ fontSize: 13, color: "#888" }}>No bank account needed — contractor will be paid by <strong>Check</strong>.</p>
                        )}
                    </div>
                )}
            </div>

            {(isError || submitError) && (
                <p className="text-danger mt-2">
                    {submitError || error?.response?.data?.message || error?.response?.data?.title || "Failed to save contractor. Please try again."}
                </p>
            )}
            {Object.keys(errors).length > 0 && (
                <div className="mt-2 p-3" style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, fontSize: 13 }}>
                    <strong style={{ color: "#856404" }}>Please fix the following before saving:</strong>
                    <ul className="mb-0 mt-1" style={{ color: "#856404" }}>
                        {Object.values(errors).map((e, i) => <li key={i}>{e.message}</li>)}
                    </ul>
                </div>
            )}
            {!isEditMode && !fromOnboarding && (
                <div style={{ margin: '16px 0', padding: '14px 18px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <input
                        type="checkbox"
                        id="sendInviteCon"
                        checked={sendInvite}
                        onChange={e => setSendInvite(e.target.checked)}
                        style={{ marginTop: 3, accentColor: '#16a34a', width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <label htmlFor="sendInviteCon" style={{ cursor: 'pointer', margin: 0 }}>
                        <span style={{ fontWeight: 600, color: '#15803d', fontSize: 14 }}>Send Portal Invitation Email</span>
                        <span style={{ display: 'block', fontSize: 12, color: '#166534', marginTop: 2 }}>
                            Contractor will receive a welcome email with login credentials for payrollnoot.com
                        </span>
                    </label>
                </div>
            )}

            {isEditMode && !fromOnboarding && (
                <div style={{ margin: '16px 0', padding: '14px 18px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10 }}>
                    <p style={{ fontWeight: 600, color: '#15803d', fontSize: 14, margin: '0 0 4px 0' }}>Portal Access</p>
                    <p style={{ fontSize: 12, color: '#166534', margin: '0 0 10px 0' }}>
                        Send a "Set My Password" email so this contractor can access their portal. Has no effect if they already have an account.
                    </p>
                    {inviteResult === 'sent' && <p style={{ color: '#15803d', fontSize: 13, margin: '0 0 8px 0' }}>✓ Invite sent successfully.</p>}
                    {inviteResult === 'exists' && <p style={{ color: '#92400e', fontSize: 13, margin: '0 0 8px 0' }}>Portal account already exists for this contractor.</p>}
                    {inviteResult === 'error' && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 8px 0' }}>Failed to send invite. Please try again.</p>}
                    <button
                        type="button"
                        style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        onClick={async () => {
                            setInviteResult(null);
                            try {
                                const inv = await inviteContractor(contractorId);
                                setInviteResult(inv?.alreadyExists ? 'exists' : 'sent');
                            } catch {
                                setInviteResult('error');
                            }
                        }}
                    >
                        Send Portal Invite
                    </button>
                </div>
            )}

            <div className={styles["bottom-container"]}>
                <Button buttonType="flat" type="submit" disabled={isPending || isSubmitting}>
                    {(isPending || isSubmitting) ? "Saving..." : (isEditMode ? "Update Contractor" : "Add Contractor")}
                </Button>
                <Button variant="text" type="button" onClick={() => navigate('/contractor')}>Cancel</Button>
            </div>
        </form>
    );
}

export default AddEditContractor;




